import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ArrowRight, X, Package } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import LanguageLink from '../routing/LanguageLink';
import categoriesData from '../data/categoriesData';
import { getFormPackages } from '../services/formPackagesService';
import Swal from 'sweetalert2';

export default function CategoryHeader() {
    const [openCategory, setOpenCategory] = useState(null);
    const [exploreOpen, setExploreOpen] = useState(false);
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [packagesError, setPackagesError] = useState(null);
    const timeoutRef = useRef(null);
    const navigate = useNavigate();
    const { lang } = useParams();
    const currentLang = lang || 'en';

    const handleMouseEnter = (id) => {
        if (window.innerWidth < 768) return;
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setOpenCategory(id);
        }, 150);
    };

    const handleMouseLeave = () => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setOpenCategory(null);
        }, 200);
    };

    const toggleMenu = (id) => {
        if (window.innerWidth < 768) {
            setOpenCategory(openCategory === id ? null : id);
        }
    };

    const closeMenu = () => setOpenCategory(null);

    const handleExploreClick = async () => {
        if (exploreOpen) {
            setExploreOpen(false);
            return;
        }

        setExploreOpen(true);

        // Only fetch if we haven't fetched before
        if (packages.length === 0 && !packagesLoading && !packagesError) {
            setPackagesLoading(true);
            setPackagesError(null);
            try {
                const data = await getFormPackages();
                setPackages(data || []);
            } catch (error) {
                setPackagesError(error.response?.data?.message || "Failed to load packages");
                Swal.fire("Error", "Failed to load packages. Please try again.", "error");
            } finally {
                setPackagesLoading(false);
            }
        }
    };

    const closeExploreMenu = () => {
        setExploreOpen(false);
    };

    const handlePackageClick = (packageId) => {
        navigate(`/${currentLang}/form-packages/${packageId}`);
        closeExploreMenu();
    };

    useEffect(() => {
        return () => clearTimeout(timeoutRef.current);
    }, []);

    return (
        <>
            <section className="py-4 bg-white border-y border-gray-200 sticky top-0 z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-4">
                    <nav className="f-col flex justify-center gap-8 md:gap-10 flex-wrap">
                        {categoriesData.map((category) => (
                            <div
                                key={category.id}
                                className="relative"
                                onMouseEnter={() => handleMouseEnter(category.id)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <button
                                    onClick={() => toggleMenu(category.id)}
                                    className="flex items-center gap-1 px-4 md:px-5 py-3 text-[18px] font-semibold text-gray-800 hover:text-blue-600 transition-colors"
                                >
                                    {category.name}
                                    <ChevronDown
                                        className={`w-4 h-4 transition-transform duration-200 ${openCategory === category.id ? 'rotate-180' : ''
                                            }`}
                                    />
                                </button>
                            </div>
                        ))}

                        {/* Explore Packages Button - Last */}
                        <div
                            className="relative"
                            onMouseEnter={() => {
                                if (window.innerWidth >= 768) {
                                    clearTimeout(timeoutRef.current);
                                    timeoutRef.current = setTimeout(() => {
                                        if (!exploreOpen) handleExploreClick();
                                    }, 150);
                                }
                            }}
                            onMouseLeave={() => {
                                if (window.innerWidth >= 768) {
                                    clearTimeout(timeoutRef.current);
                                    timeoutRef.current = setTimeout(() => {
                                        setExploreOpen(false);
                                    }, 200);
                                }
                            }}
                        >
                            <button
                                onClick={handleExploreClick}
                                className="flex items-center gap-1 px-4 md:px-5 py-3 text-[18px] font-semibold text-gray-800 hover:text-blue-600 transition-colors"
                            >
                                Explore Packages
                                <ChevronDown
                                    className={`w-4 h-4 transition-transform duration-200 ${exploreOpen ? 'rotate-180' : ''}`}
                                />
                            </button>
                        </div>
                    </nav>
                </div>
            </section>

            {openCategory && (
                <div
                    className="fixed inset-0 z-50 flex items-start justify-center pt-[10rem]"
                    onClick={closeMenu}
                    onMouseEnter={() => clearTimeout(timeoutRef.current)}
                    onMouseLeave={handleMouseLeave}
                >
                    <div
                        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-10 duration-300"
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={() => clearTimeout(timeoutRef.current)}
                        onMouseLeave={handleMouseLeave}
                    >
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h3 className="text-xl font-bold text-gray-900">
                                {categoriesData.find((c) => c.id === openCategory)?.name}
                            </h3>
                            <button
                                onClick={closeMenu}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="max-h-96 overflow-y-auto py-3">
                            <ul className="space-y-1">
                                {categoriesData
                                    .find((c) => c.id === openCategory)
                                    ?.items.map((item, index) => (
                                        <li key={index}>
                                            <LanguageLink
                                                to={item.href}
                                                onClick={closeMenu}
                                                className="flex items-start gap-3 px-6 py-3 hover:bg-blue-50 transition-colors group mx-3 rounded-lg"
                                            >
                                                <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                <div className="flex-1">
                                                    <div className="font-medium text-gray-900 group-hover:text-blue-600">
                                                        {item.title}
                                                    </div>
                                                    {item.subtitle && (
                                                        <div className="text-sm text-gray-500 mt-0.5">
                                                            {item.subtitle}
                                                        </div>
                                                    )}
                                                </div>
                                            </LanguageLink>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Explore Packages Dropdown */}
            {exploreOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-start justify-center pt-[10rem]"
                    onClick={closeExploreMenu}
                    onMouseEnter={() => clearTimeout(timeoutRef.current)}
                    onMouseLeave={() => {
                        if (window.innerWidth >= 768) {
                            clearTimeout(timeoutRef.current);
                            timeoutRef.current = setTimeout(() => {
                                setExploreOpen(false);
                            }, 200);
                        }
                    }}
                >
                    <div
                        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-10 duration-300"
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={() => clearTimeout(timeoutRef.current)}
                        onMouseLeave={() => {
                            if (window.innerWidth >= 768) {
                                clearTimeout(timeoutRef.current);
                                timeoutRef.current = setTimeout(() => {
                                    setExploreOpen(false);
                                }, 200);
                            }
                        }}
                    >
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <Package className="w-5 h-5 text-blue-600" />
                                Packages
                            </h3>
                            <button
                                onClick={closeExploreMenu}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="max-h-96 overflow-y-auto py-3">
                            {packagesLoading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                                    <p className="text-gray-600">Loading packages...</p>
                                </div>
                            ) : packagesError ? (
                                <div className="px-6 py-8 text-center">
                                    <p className="text-red-600 mb-4">{packagesError}</p>
                                    <button
                                        onClick={handleExploreClick}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : packages.length === 0 ? (
                                <div className="px-6 py-12 text-center">
                                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600">No packages available at the moment.</p>
                                </div>
                            ) : (
                                <ul className="space-y-1">
                                    {packages.map((pkg) => (
                                        <li key={pkg.id}>
                                            <button
                                                onClick={() => handlePackageClick(pkg.id)}
                                                className="w-full flex items-start gap-3 px-6 py-3 hover:bg-blue-50 transition-colors group mx-3 rounded-lg text-left"
                                            >
                                                <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-gray-900 group-hover:text-blue-600">
                                                        {pkg.name}
                                                    </div>
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}